import {
  DisplayMarket,
  MARKETS_PAGE_SIZE,
  MarketsByProtocol,
  MarketsOptions,
  MarketsSort,
} from "@silo/types";
import { Array, Option, Order, pipe } from "effect";
import { fuseSearch } from "../array";
import { match } from "ts-pattern";

interface TransformMarketsParams {
  marketsByProtocol: MarketsByProtocol;
  marketsOptions: MarketsOptions;
}

interface TransformMarketsReturnType {
  allMarkets: DisplayMarket[];
  markets: DisplayMarket[];
  hasMorePages: boolean;
}

export function transformMarkets({
  marketsByProtocol,
  marketsOptions,
}: TransformMarketsParams): TransformMarketsReturnType {
  const { protocolKey, search, sort, pages } = marketsOptions;

  // 1. get markets for the protocol
  const allMarkets = pipe(
    marketsByProtocol,
    Array.findFirst((item) => item.protocolKey === protocolKey),
    Option.map((item) => item.markets),
    Option.getOrElse((): DisplayMarket[] => []),
    // remove readonly modifier
    (arr) => [...arr]
  );

  // 2. filter markets by search criteria
  const filteredMarkets = pipe(
    search,
    Option.fromNullable,
    Option.map((pattern) => {
      return fuseSearch(allMarkets, pattern, [
        {
          name: "marketSymbol",
          getFn: (market) => market.marketSymbol,
        },
        {
          name: "marketName",
          getFn: (market) => market.marketName,
        },
      ]);
    }),
    Option.getOrElse(() => allMarkets)
  );

  // 3. sort markets
  const sortedMarkets = pipe(
    sort,
    Option.fromNullable,
    Option.map((sort) => sortMarkets(filteredMarkets, sort)),
    Option.getOrElse(() => filteredMarkets)
  );

  // 4. slice to N pages
  return pipe(
    pages,
    Option.fromNullable,
    Option.map(Math.round),
    Option.map((pages) => Math.max(pages, 1)),
    Option.map((pages): TransformMarketsReturnType => {
      const numberOfMarkets = pages * MARKETS_PAGE_SIZE;

      return {
        allMarkets,
        markets: sortedMarkets.slice(0, numberOfMarkets),
        hasMorePages: numberOfMarkets < sortedMarkets.length,
      };
    }),
    Option.getOrElse(
      (): TransformMarketsReturnType => ({
        allMarkets,
        markets: sortedMarkets,
        hasMorePages: false,
      })
    )
  );
}

const marketSymbolOrder: Order.Order<DisplayMarket> = Order.mapInput(
  Order.string,
  (market) => market.marketSymbol.toLowerCase()
);

const liquidityUsdOrder: Order.Order<DisplayMarket> = Order.mapInput(
  Order.bigint,
  (market) => market.liquidityUsd
);

const baseDepositTotalAprOrder: Order.Order<DisplayMarket> = Order.mapInput(
  Order.bigint,
  (market) => market.baseAsset.depositTotalApr
);

const baseDebtTotalAprOrder: Order.Order<DisplayMarket> = Order.mapInput(
  Order.bigint,
  (market) => market.baseAsset.debtTotalApr
);

const firstBridgeDepositTotalAprOrder: Order.Order<DisplayMarket> =
  Order.mapInput(
    Order.bigint,
    (market) => market.bridgeAssets[0].depositTotalApr
  );

const firstBridgeDebtTotalAprOrder: Order.Order<DisplayMarket> = Order.mapInput(
  Order.bigint,
  (market) => market.bridgeAssets[0].debtTotalApr
);

const secondBridgeDepositTotalAprOrder: Order.Order<DisplayMarket> =
  Order.mapInput(
    Order.bigint,
    (market) => market.bridgeAssets[1]?.depositTotalApr ?? 0n
  );

const secondBridgeDebtTotalAprOrder: Order.Order<DisplayMarket> =
  Order.mapInput(
    Order.bigint,
    (market) => market.bridgeAssets[1]?.debtTotalApr ?? 0n
  );

function sortMarkets(
  markets: DisplayMarket[],
  { field, direction }: NonNullable<MarketsSort>
): DisplayMarket[] {
  const order = match(field)
    .with("marketSymbol", () => marketSymbolOrder)
    .with("liquidityUsd", () => liquidityUsdOrder)
    .with("baseDepositTotalApr", () => baseDepositTotalAprOrder)
    .with("baseDebtTotalApr", () => baseDebtTotalAprOrder)
    .with("firstBridgeDepositTotalApr", () => firstBridgeDepositTotalAprOrder)
    .with("firstBridgeDebtTotalApr", () => firstBridgeDebtTotalAprOrder)
    .with("secondBridgeDepositTotalApr", () => secondBridgeDepositTotalAprOrder)
    .with("secondBridgeDebtTotalApr", () => secondBridgeDebtTotalAprOrder)
    .exhaustive();

  const directedOrder = match(direction)
    .with("asc", () => order)
    .with("desc", () => Order.reverse(order))
    .exhaustive();

  return pipe(markets, Array.sort(directedOrder));
}
